import React, { useEffect, memo, useState, useCallback, useMemo } from 'react';
import { Text, View, StyleSheet, Pressable } from 'react-native';
import { I18n, Select, CTAButton } from '../../components';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../../actions/statusTypes';
import { THEME, FONT, __WEB__ } from '../../../constants/styles';

import { findMyUsersAndSignIn, requestEmailLinkForOktaAccountLink, loginWithUserId } from './slice';
import { logoutUser } from '../actions';

import StyledInput from '../styled_input';
import { AuthForm, ErrorView } from '../auth_views';
import CommonStylesNative from '../common_styles';

const AccountForm = memo((props) => {
  useEffect(() => {
    if (!props.tokenError && props.oktaAuthToken && props.getUsersStatus === PENDING) {
      props.actions.findMyUsersAndSignIn();
    }
  }, [props.tokenError, props.getUsersStatus, props.oktaAuthToken]);
  const [email, setEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const handleEmailSubmit = useCallback(() => {
    if (email) {
      props.actions.requestEmailLinkForOktaAccountLink({ email: email.toLowerCase().trim() });
    }
  }, [email]);

  const userOptions = useMemo(() => {
    return (
      props.users?.map((user) => ({
        label: user.email,
        id: user.userId,
      })) || []
    ).concat({ label: 'Sign in with a different user', id: null });
  }, [props.users]);

  useEffect(() => {
    setSelectedUser(userOptions[0]?.id || null);
  }, [userOptions]);

  const handleLoginUser = useCallback(() => {
    if (selectedUser) {
      props.actions.loginWithUserId({ userId: selectedUser });
    }
  }, [selectedUser]);

  const logoutFromOkta = useCallback(() => {
    props.actions.logoutUser(true, false, false);
  }, []);

  const errorMessage =
    props.getUsersError?.message ||
    props.requestEmailLinkError?.message ||
    props.linkMyAccountError?.message ||
    props.loginUserError?.message ||
    props.acceptInviteError?.message ||
    (props.tokenError ? I18n.t('auth.militaryText') : null);
  const successMessage =
    props.requestEmailLinkStatus === SUCCESS
      ? 'An email has been sent to you with a link to sign in.'
      : null;

  function renderUserSelect() {
    if (!!successMessage || props.getUsersStatus !== SUCCESS) return null;
    if (props.users?.length === 1 && !props.wasLoggedIn) return null;
    if (!!props.users?.length) {
      // if (true) {
      return (
        <View style={[{ zIndex: 100 }, __WEB__ ? null : CommonStylesNative.inputsContainer]}>
          <Select
            options={userOptions}
            label={'Login User'}
            value={selectedUser}
            onSelect={setSelectedUser}
            webMode={true}
            nativeButtonStyle={{ borderColor: 'white' }}
            valueStyle={__WEB__ ? null : { color: 'white' }}
            fieldLabelStyle={__WEB__ ? null : { color: 'white' }}
            nativeArrowIconStyle={{ tintColor: 'white' }}
          />

          {!!selectedUser ? (
            <CTAButton
              style={[
                __WEB__ ? { width: 200 } : { ...CommonStylesNative.button, paddingVertical: 0 },
              ]}
              fontStyle={__WEB__ ? null : CommonStylesNative.buttonText}
              onPress={handleLoginUser}
              label={'Login'}
              disabled={!selectedUser}
            />
          ) : (
            <View style={{ marginTop: 20 }} />
          )}
        </View>
      );
    }
    return null;
  }

  function renderEmailEntry() {
    if (!!successMessage || props.getUsersStatus !== SUCCESS) return null;
    if (!props.users?.length || !selectedUser) {
      // if (true) {
      return (
        <View style={__WEB__ ? null : CommonStylesNative.inputsContainer}>
          <Text style={[THEME.textRegular, { marginBottom: 25 }]}>
            Please enter your email address to receive a sign in link.
          </Text>
          <StyledInput
            label={I18n.ac('form.emailAddress')}
            value={email}
            onChangeText={setEmail}
            onSubmitEditing={handleEmailSubmit}
            autoComplete={'email'}
            keyboardType="email-address"
            returnKeyType="next"
            testID="email"
          />
          <CTAButton
            style={[
              __WEB__ ? { width: 200 } : { ...CommonStylesNative.button, paddingVertical: 0 },
            ]}
            fontStyle={__WEB__ ? null : CommonStylesNative.buttonText}
            onPress={handleEmailSubmit}
            label={'Login'}
            disabled={!email.trim()}
          />
        </View>
      );
    }
    return null;
  }
  return (
    <AuthForm
      {...props}
      military={true}
      showPrivacy={true}
      showActivityOverlay={
        props.getUsersStatus === REQUESTING ||
        props.requestEmailLinkStatus === REQUESTING ||
        props.loginUserStatus === REQUESTING ||
        props.linkMyAccountStatus === REQUESTING ||
        props.acceptInviteStatus === REQUESTING
      }
      skipDisclaimerLinks={!!props.tokenError}
      autoAcceptAFTerms={!!__WEB__}
    >
      {(!!errorMessage || !!successMessage) && (
        <View style={{ marginTop: __WEB__ ? 0 : 50 }}>
          <ErrorView errorMessage={errorMessage} successMessage={successMessage} />
        </View>
      )}

      {renderUserSelect()}
      {renderEmailEntry()}

      <Pressable onPress={logoutFromOkta}>
        <Text style={[THEME.linkStyle, THEME.textMedium, { marginTop: 25 }]}>Logout from Okta</Text>
      </Pressable>
    </AuthForm>
  );
});

const styles = StyleSheet.create({
  paneWrapper: {
    flex: 1,
    justifyContent: 'center',
    maxWidth: 400,
    width: '100%',
    marginHorizontal: 'auto',
  },
  errorText: {
    ...FONT.semibold,
    fontSize: 20,
    color: 'white',
    marginLeft: 15,
    textAlign: 'center',
    marginBottom: 50,
    marginTop: 50,
  },
});

export default connect(
  (state) => ({
    oktaAuthToken: state.user.oktaAuthToken,
    users: state.screens.oktaAuth.users,
    getUsersStatus: state.screens.oktaAuth.getUsersStatus,
    getUsersError: state.screens.oktaAuth.getUsersError,
    requestEmailLinkStatus: state.screens.oktaAuth.requestEmailLinkStatus,
    requestEmailLinkError: state.screens.oktaAuth.requestEmailLinkError,
    loginUserStatus: state.screens.oktaAuth.loginUserStatus,
    loginUserError: state.screens.oktaAuth.loginUserError,
    linkMyAccountStatus: state.screens.oktaAuth.linkMyAccountStatus,
    linkMyAccountError: state.screens.oktaAuth.linkMyAccountError,
    acceptInviteStatus: state.screens.oktaAuth.acceptInviteStatus,
    acceptInviteError: state.screens.oktaAuth.acceptInviteError,
    wasLoggedIn: state.application.wasLoggedIn,
  }),
  (dispatch) => ({
    actions: bindActionCreators(
      {
        findMyUsersAndSignIn,
        requestEmailLinkForOktaAccountLink,
        loginWithUserId,
        logoutUser,
      },
      dispatch
    ),
  })
)(AccountForm);
